import Text from '@cartwhl/ui/base/Text';

import { getBoneNameClassName, getBoneNameTone } from '../../../presentations/boneName';
import { BonePropertiesBadge } from '../../../presentations/BonePropertiesBadge';
import { hasBoneProperties } from '../../../presentations/properties';
import { ExcludedBoneIcon, TransformNodeIcon } from '../../../presentations/icons';
import {
  ROW_VERTICAL_PADDING_PX,
  TREE_NODE_BASE_LEFT_OFFSET_PX,
  TREE_NODE_INDENT_STEP_PX,
} from '../constants';
import type { BoneRowProps } from '../types';
import ChainOverrideControl from './ChainOverrideControl';
import ChainPill from './ChainPill';
import ExcludedOverrideControl from './ExcludedOverrideControl';
import RoleOverrideControl from './RoleOverrideControl';
import RoleTag from './RoleTag';

export default function BoneRow({
  node,
  bone,
  lookups,
  presentRoleNames,
  presentChainNames,
  excludedSet,
  selectedNodeId,
  selectedBoneIndex,
  isAncestorOfSelected,
  expanded,
  hasVisibleDescendants,
  depth,
  rowGridTemplateColumns,
  showExcludedColumn,
  showRolesColumn,
  showChainColumn,
  onSelectNode,
  onToggleExpanded,
  onToggleBoneExcluded,
  onOverrideBoneRole,
  onClearBoneChain,
  onBeginChainPathSelection,
  onCancelChainPathSelection,
  onAddBoneToChain,
  onRemoveBoneFromChain,
  pendingChainPathSelection,
  mappingIoExtras,
}: BoneRowProps) {
  const isBoneNode = node.kind === 'bone' && bone !== null;
  const hasChildren = hasVisibleDescendants;
  const isExpanded = expanded.has(node.id);
  const isSelected =
    node.kind === 'transform'
      ? selectedNodeId === node.id
      : isBoneNode && node.boneIndex === selectedBoneIndex;
  const isExcluded = isBoneNode
    ? excludedSet.has(node.boneIndex)
    : false;
  const boneNameTone = isBoneNode
    ? getBoneNameTone(bone, isExcluded)
    : 'muted';
  const nodeLabel = bone?.name ?? node.name;

  const role = isBoneNode ? lookups.boneToRole.get(node.boneIndex) : undefined;
  const confidence = isBoneNode
    ? (lookups.boneToConfidence.get(node.boneIndex) ?? null)
    : null;
  const chainName = isBoneNode
    ? lookups.boneToChain.get(node.boneIndex)
    : undefined;
  const hasProperties = isBoneNode
    ? hasBoneProperties(bone, mappingIoExtras)
    : false;

  return (
    <div
      data-outliner-node-id={node.id}
      className={`grid items-center gap-x-2 rounded-lg px-1.5 leading-5 transition-colors ${
        isBoneNode ? 'cursor-pointer' : 'cursor-default'
      } ${
        isSelected
          ? 'bg-blue-50/40 text-gray-90 dark:bg-blue-60/35 dark:text-white'
          : isAncestorOfSelected
            ? 'bg-blue-50/10 text-gray-90 dark:bg-blue-60/20 dark:text-gray-15'
          : 'hover:bg-gray-15/70 dark:hover:bg-gray-75/70'
      }`}
      style={{
        gridTemplateColumns: rowGridTemplateColumns,
        paddingTop: `${ROW_VERTICAL_PADDING_PX}px`,
        paddingBottom: `${ROW_VERTICAL_PADDING_PX}px`,
      }}
      onClick={() => {
        onSelectNode(node.id, isBoneNode ? node.boneIndex : null);
      }}
    >
      <div
        className="flex min-w-0 items-center gap-1"
        style={{
          paddingLeft: `${depth * TREE_NODE_INDENT_STEP_PX + TREE_NODE_BASE_LEFT_OFFSET_PX}px`,
        }}
      >
        {hasChildren ? (
          <button
            type="button"
            className="text-primary hover:text-primary flex h-4 w-4 shrink-0 items-center justify-center rounded border-0 bg-transparent p-0 outline-none ring-0 focus:border-0 focus:outline-none focus:ring-0 focus-visible:border-0 focus-visible:outline-none focus-visible:ring-0"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpanded(node.id, e.shiftKey);
            }}
          >
            {isExpanded ? '\u25BE' : '\u25B8'}
          </button>
        ) : (
          <span className="inline-block h-4 w-4 shrink-0" />
        )}

        <Text
          as="span"
          variant="studio/body/medium/xs"
          className={`min-w-0 block flex-1 ${
            isBoneNode
              ? getBoneNameClassName(boneNameTone, { italicWhenMuted: true })
              : 'text-blue-60 dark:text-blue-50'
          }`}
        >
          <span className="flex min-w-0 items-center gap-1">
            {isBoneNode ? (
              <>
                {hasProperties && <BonePropertiesBadge />}
                {isExcluded && <ExcludedBoneIcon className="h-2.5 w-2.5 shrink-0" />}
              </>
            ) : (
              <TransformNodeIcon className="h-2.5 w-2.5 shrink-0" />
            )}
            <span className="min-w-0 truncate">{nodeLabel}</span>
          </span>
        </Text>
      </div>

      {showExcludedColumn && (
        <div className="flex min-w-0 items-center justify-center">
          {isBoneNode && onToggleBoneExcluded ? (
            <ExcludedOverrideControl
              excluded={isExcluded}
              onToggle={() => onToggleBoneExcluded(bone.internalRef)}
            />
          ) : (
            isBoneNode &&
            isExcluded && (
              <span
                className="text-secondary inline-flex h-5 w-full items-center justify-center"
                aria-hidden="true"
              >
                <ExcludedBoneIcon className="h-3.5 w-3.5" />
              </span>
            )
          )}
        </div>
      )}

      {showRolesColumn && (
        <div className="min-w-0">
          {isBoneNode && onOverrideBoneRole ? (
            <RoleOverrideControl
              role={role ?? null}
              confidence={confidence}
              presentRoleNames={presentRoleNames}
              onRoleChange={(nextRole) =>
                onOverrideBoneRole(bone.internalRef, nextRole)
              }
            />
          ) : (
            isBoneNode &&
            role && <RoleTag role={role} confidence={confidence} />
          )}
        </div>
      )}

      {showChainColumn && (
        <div className="min-w-0">
          {isBoneNode && onBeginChainPathSelection && onAddBoneToChain ? (
            <ChainOverrideControl
              chainName={chainName ?? null}
              presentChainNames={presentChainNames}
              pendingChainPathSelection={pendingChainPathSelection}
              onTriggerSelectBone={() => onSelectNode(node.id, node.boneIndex)}
              onClearChain={
                onClearBoneChain && chainName
                  ? () => onClearBoneChain(chainName)
                  : undefined
              }
              onBeginStartEnd={(targetChainName) =>
                onBeginChainPathSelection(targetChainName)
              }
              onCancelStartEnd={onCancelChainPathSelection}
              onAddToChain={(targetChainName) =>
                onAddBoneToChain(targetChainName, bone.internalRef)
              }
              onRemoveFromChain={
                onRemoveBoneFromChain && chainName
                  ? () => onRemoveBoneFromChain(chainName, bone.internalRef)
                  : undefined
              }
            />
          ) : (
            isBoneNode &&
            chainName && <ChainPill chainName={chainName} />
          )}
        </div>
      )}
    </div>
  );
}
